import { ServiceType } from './serviceTypes';
import { AppointmentType } from 'src/services/availability';
import { AppointmentServiceType } from 'src/services/booking';

const afterHoursServices = [ServiceType['After hours/Home Visit'], ServiceType['After Hours Phone Consult']];

const telehealthServiceTypeIds = [ServiceType.Telehealth];

export const newPatientRegex = /\s*\W?\s?[Nn][Ee][Ww] [Pp][Aa][Tt][Ii][Ee][Nn][Tt]\W?\s*/;

export enum ExpressTelehealthPurpose {
    Unknown = 'Unknown',
    Script = 'Script',
    MedicalCertificate = 'MedicalCertificate',
    Referral = 'Referral',
    Diagnostics = 'Diagnostics',
}

export function isMedibankFluCampaign(appointmentType: AppointmentType) {
    return appointmentType.appointmentName === 'Medibank Flu Vaccination';
}

export function isTelehealthNow(appointmentType: AppointmentType) {
    return appointmentType.appointmentName === 'Telehealth Now';
}

export function isExpressTelehealth(appointmentType: AppointmentType) {
    return (
        appointmentType?.appointmentBPName.toUpperCase() === 'EXPRESS TELEHEALTH' &&
        isTelehealthBulkBilled(appointmentType)
    );
}

export function filterToStandardAppointmentTypes(data: AppointmentType[]): AppointmentType[] {
    return data?.filter(
        (apt) => !afterHoursServices.includes(apt.serviceTypeId) && !isMedibankFluCampaign(apt) && !isTelehealthNow(apt)
    );
}

export function filterToAfterHoursAppointmentTypes(data: AppointmentType[]): AppointmentType[] {
    return data?.filter((apt) => afterHoursServices.includes(apt.serviceTypeId));
}

export function filterToServiceType(data: AppointmentType[], ...serviceTypes: ServiceType[]): AppointmentType[] {
    return data?.filter((apt) => serviceTypes.includes(apt.serviceTypeId));
}

export function determineAppointmentServiceType(appointmentType: AppointmentType) {
    if (isAfterHours(appointmentType)) {
        return AppointmentServiceType.AfterHours;
    }

    if (isExpressTelehealth(appointmentType)) {
        return AppointmentServiceType.ExpressTelehealth;
    }

    return isFaceToFace(appointmentType) ? AppointmentServiceType.FaceToFace : AppointmentServiceType.Telehealth;
}

export function isFaceToFace(appointmentType: AppointmentType) {
    return (
        appointmentType.isF2F ||
        (!telehealthServiceTypeIds.includes(appointmentType.serviceTypeId) && !isAfterHours(appointmentType))
    );
}

export function isAfterHours(appointmentType: AppointmentType) {
    return afterHoursServices.includes(appointmentType.serviceTypeId);
}

export function isTelehealthBulkBilled(appointmentType: AppointmentType) {
    return (
        !isFaceToFace(appointmentType) &&
        appointmentType.postPaymentRequired &&
        Number(appointmentType.postBillingFees) > 0 &&
        Number(appointmentType.postBillingFees) < 1
    );
}

export function isPaymentRequired(appointmentType: AppointmentType) {
    if (!appointmentType) {
        return undefined;
    }
    return (
        (appointmentType.prePaymentRequired && Number(appointmentType.prePaymentFees) > 0) ||
        (appointmentType.postPaymentRequired && Number(appointmentType.postBillingFees) > 0)
    );
}

export function getPaymentMethod(appointmentType: AppointmentType) {
    if (!appointmentType) {
        return undefined;
    }
    if (appointmentType.prePaymentRequired && Number(appointmentType.prePaymentFees) > 0) {
        return 2;
    }
    if (appointmentType.postPaymentRequired && Number(appointmentType.postBillingFees) > 0) {
        return 3;
    }
}

export function expressTelehealthPurpose(appointmentType?: AppointmentType): ExpressTelehealthPurpose {
    if (appointmentType?.appointmentName.toLocaleUpperCase().includes('SCRIPT')) {
        return ExpressTelehealthPurpose.Script;
    }
    if (appointmentType?.appointmentName.toLocaleUpperCase().includes('CERTIFICATE')) {
        return ExpressTelehealthPurpose.MedicalCertificate;
    }
    if (appointmentType?.appointmentName.toLocaleUpperCase().includes('REFERRAL')) {
        return ExpressTelehealthPurpose.Referral;
    }
    if (
        appointmentType?.appointmentName.toLocaleUpperCase().includes('PATHOLOGY') ||
        appointmentType?.appointmentName.toLocaleUpperCase().includes('IMAGING')
    ) {
        return ExpressTelehealthPurpose.Diagnostics;
    }
    return ExpressTelehealthPurpose.Unknown;
}
